import { Add, Spa } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { useStore } from "@priolo/jon";
import SelectorBase from "components/controls/base/SelectorBase";
import DatePicker from "components/controls/fields/DatePicker";
import NumberField from "components/controls/fields/NumberField";
import TextAreaField from "components/controls/fields/TextAreaField";
import Paragraph from "components/controls/form/Paragraph";
import Modal from "components/controls/Modal";
import dayjs from "dayjs";
import LostTrolley from "images/lostTrolley";
import { ChangeEvent, FunctionComponent, useRef } from "react";
import incidentSo from "stores/incidents";
import { CropLot } from "types/CropLot";
import { Cycle } from "types/Cycle";
import { IncidentType } from "types/Incident";
import IncidentAttachments from "./attach/IncidentAttachments";
import LayersPositionSelector from "./LayersPositionSelector";
import { Attachment } from "types/Attachment";



interface Props {
    cycle?: Cycle
}
const IncidentDialog: FunctionComponent<Props> = ({
    cycle
}) => {

    // STORE
    useStore(incidentSo)

    // HOOKs
    const fileRef = useRef<HTMLInputElement>(null);

    // HANDLEs
    const handleChangeProp = (prop: any) => incidentSo.setSelect({ ...incidentSo.state.select, ...prop })
    const handleAttachmentsChange = (attachments: Attachment[]) => {
        incidentSo.setSelect({ ...incidentSo.state.select, attachments })
    }
    const handleChangeCropLot = (cropLot: CropLot) => {
        if (!cropLot) return
        incidentSo.state.select.lostTraysPerCropLot = [{ ...traysPerCropLots, cropLotId: cropLot.id }]
        incidentSo._update()
    }
    const handleChangeLost = (numberOfLostTrays: number) => {
        incidentSo.state.select.lostTraysPerCropLot = [{ ...traysPerCropLots, numberOfLostTrays: numberOfLostTrays }]
        incidentSo._update()
    }

    const handleAddAttachment = () => {
		fileRef.current?.click();
	}
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;
    
            const newAttach: Attachment = {
                name: file.name,
                contentType: file.type,
                temporaryUrl: URL.createObjectURL(file), // PREVIEW
                thumbnailTemporaryUrl: "",
                file: file,
            }
            handleAttachmentsChange?.([...attachments, newAttach])
            e.target.value = ""
        }
    

    const handleClose = () => incidentSo.setEditDialogOpen(false)
    const handleSave = () => {
        incidentSo.save()
        incidentSo.setEditDialogOpen(false)
    }


    // RENDER
    const incident = incidentSo.state.select
    if (!incident || !cycle) return null
    const isNew = incident.id == null
    const traysPerCropLots = incident?.lostTraysPerCropLot?.[0]
    const attachments = incident.attachments || []

    const cropLotId = traysPerCropLots?.cropLotId
    const lostTraysNumber = traysPerCropLots?.numberOfLostTrays

    const canSave = !!cropLotId && !!(incident.positionOfTrolleys?.length > 0) && !!incident.incidentType
    const flex = 30

    return <Modal
        icon={<LostTrolley />}
        title={isNew ? "CREATE INCIDENT" : "EDIT INCIDENT"}
        isOpen={incidentSo.state.editDialogOpen}
        onClose={handleClose}
        actionsRender={<>
            <Button color="secondary" startIcon={<Add />}
                onClick={handleAddAttachment}
            >ATTACHMENT</Button>

            <input type="file" ref={fileRef} hidden onChange={handleFileChange} accept="image/*" />
            <div style={{ flex: "1 1 auto" }} />

            <Button
                onClick={handleClose}
            >CANCEL</Button>

            <Button
                onClick={handleSave}
                color="secondary"
                disabled={!canSave}
            >SAVE</Button>
        </>}
    >

        <Box sx={{ display: "flex", flexDirection: "column" }}>

            <Paragraph title={"DATE"} flex={flex}>
                <DatePicker
                    value={incident.dateOfOccurrence}
                    onChange={(value: dayjs.Dayjs) =>
                        handleChangeProp({ dateOfOccurrence: value.format("YYYY-MM-DD") })
                    }
                />
            </Paragraph>

            <Paragraph title={"INCIDENT TYPE"} flex={flex}>
                <SelectorBase sx={{ flex: 1 }}
                    title={"INCIDENT TYPE"}
                    placeholder="SELECT A TYPE"
                    icon={<LostTrolley />}
                    items={incidentSo.state.types}
                    idSelect={incident.incidentType}
                    onChange={(type: IncidentType) => handleChangeProp({ incidentType: type?.id })}
                    fnTextFromItem={(type: IncidentType) => type?.label?.toUpperCase() ?? "--"}
                    fnIdFromItem={(type: IncidentType) => type?.id}
                />
            </Paragraph>

            <Paragraph title={"CROP LOT"} flex={flex}>
                <SelectorBase sx={{ flex: 1 }}
                    title={"CROP LOTS"}
                    placeholder="SELECT A CROP-LOT"
                    icon={<Spa />}
                    items={cycle.cropLots ?? []}
                    idSelect={cropLotId}
                    onChange={handleChangeCropLot}
                    fnTextFromItem={(cropLot: CropLot) => cropLot.cropName}
                    fnIdFromItem={(cropLot: CropLot) => cropLot.id}
                />
            </Paragraph>

            <Paragraph title={"TRAYS LOST"} flex={flex}>
                <NumberField sx={{ width: "146px" }}
                    min={1}
                    value={lostTraysNumber}
                    onChangeNumber={handleChangeLost}
                    disabled={!cropLotId}
                />
            </Paragraph>

            <Paragraph title={"LAYERS"} flex={flex}>
                <LayersPositionSelector sx={{ flex: 1 }}
                    cycle={cycle}
                    layersSelect={incident.positionOfTrolleys}
                    onChangeSelect={positionOfTrolleys => handleChangeProp({ positionOfTrolleys })}
                />
            </Paragraph>

            <Paragraph
                title={"NOTES"}
                flex={flex}
                sxLabel={{ alignSelf: 'start', marginTop: '5px' }}
            >
                <TextAreaField sx={{ flex: 1 }}
                    title="NOTES"
                    placeholder="WRITE SOME NOTES"
                    multiline rows={6}
                    value={incident.notes}
                    onChange={e => handleChangeProp({ notes: e.target.value })}
                />
            </Paragraph>

            <Paragraph
                title={"ATTACHMENTS"}
                flex={flex}
                sxLabel={{ alignSelf: 'start', marginTop: '5px' }}
            >
                <IncidentAttachments
                    cycle={cycle}
                    attachments={attachments}
                    onAttachmentsChange={handleAttachmentsChange}
                />
            </Paragraph>

        </Box>
    </Modal>
}

export default IncidentDialog